import { useCallback, useMemo } from "react";
import { Form, NavDropdown } from "react-bootstrap";
import { useTerritories } from "../../contexts";
import { Territory } from "../../models";

interface TerritoryDropdownProps {
    isMobile?: boolean;
}

export function TerritoryDropdown({ isMobile }: TerritoryDropdownProps) {
    const {
        allOfficesSelected,
        handleSelectAllOffices,
        selectedOffices,
        selectedTerritories,
        territories,
        toggleOffice,
        toggleTerritory,
    } = useTerritories();

    const title = useMemo(() => {
        if (allOfficesSelected) {
            return "All Offices";
        }

        if (selectedTerritories.length === 1) {
            const selectedTerritory = selectedTerritories[0];
            if (selectedOffices.length === selectedTerritory.offices.length) {
                return selectedTerritory.name;
            }
            if (selectedOffices.length === 1) {
                return selectedOffices[0].name;
            }
            if (selectedOffices.length > 1 && selectedTerritory.offices.length > 2) {
                return `${selectedTerritory.name} (${selectedOffices.length} Offices)`;
            }
        }

        if (selectedTerritories.length === 2) {
            const firstTerritoryOffices = selectedTerritories[0].offices.length;
            const secondTerritoryOffices = selectedTerritories[1].offices.length;
            if (selectedOffices.length === firstTerritoryOffices + secondTerritoryOffices) {
                return `${selectedTerritories[0].label} & ${selectedTerritories[1].label}`;
            }
            return `${selectedTerritories[0].label} & ${selectedTerritories[1].label} (${selectedOffices.length} Offices)`;
        }

        if (selectedOffices.length === 0) {
            return <span className="text-danger">No Offices Selected</span>;
        }

        return `${selectedOffices.length} Offices`;
    }, [allOfficesSelected, selectedOffices, selectedTerritories]);

    const getTerritoryOption = useCallback(
        (territory: Territory): string => {
            const selectedTerritory = selectedTerritories.find((t) => t.id === territory.id);
            if (!selectedTerritory) return territory.name;

            const selectedOfficesForTerritory = selectedOffices.filter((r) => r.territoryId === territory.id);

            if (
                selectedOfficesForTerritory.length === territory.offices.length ||
                !selectedOfficesForTerritory.length
            ) {
                return territory.name;
            }

            return `${territory.name} (${selectedOfficesForTerritory.length}/${territory.offices.length})`;
        },
        [selectedOffices, selectedTerritories]
    );

    return (
        <NavDropdown
            title={title}
            autoClose="outside"
            className={`territory-filter ${isMobile ? "d-lg-none" : "d-none d-lg-block"}`}
        >
            <NavDropdown.Item onClick={handleSelectAllOffices} active={allOfficesSelected}>
                All Offices
            </NavDropdown.Item>
            <NavDropdown.Divider />
            {territories.map((territory) => (
                <div className="d-flex align-items-center ps-2 territory-option" key={`territory-${territory.id}`}>
                    <Form.Check
                        inline
                        type="checkbox"
                        key={territory.id}
                        checked={selectedTerritories.some((t) => t.id == territory.id)}
                        onClick={() => toggleTerritory(territory.id)}
                    />
                    <NavDropdown
                        title={getTerritoryOption(territory)}
                        autoClose="outside"
                        className="office-dropdown"
                        drop="end"
                    >
                        {territory.offices.map((office) => (
                            <NavDropdown.Item
                                key={`territory-${territory.id}-office-${office.id}`}
                                onClick={() => toggleOffice(office.id, territory.id)}
                                active={selectedOffices.some((r) => r.id == office.id)}
                            >
                                {office.name}
                            </NavDropdown.Item>
                        ))}
                    </NavDropdown>
                </div>
            ))}
        </NavDropdown>
    );
}
